import { Col, DatePicker, Form, message, Row, Space } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DraggableModal } from '../../../../components/DraggableModal'
import dayjs, { Dayjs } from 'dayjs'
import { SelectTerritorialUnits } from '../../../../components/selects'
import { dateFormat } from '../../../../utils'
import {
	EntFertilizerUsage,
	FertilizerUsageCrops,
} from '../../../../models/chemicalization/fertilizerUsage'

import { FertilizerUsageItems } from './FertilizerUsageItems'

enum FormField {
	id,
	territorialUnitRegionId,
	periodStart,
	periodEnd,
}

interface IProps {
	show: boolean
	fertilizerUsage: EntFertilizerUsage
	onHide: () => void
	onSubmit: (pos: EntFertilizerUsage) => Promise<boolean>
}

export const AddFertilizerUsage = (props: IProps) => {
	const { t } = useTranslation()
	const [form] = useForm()
	const [loading, setLoading] = useState<boolean>(false)
	const [startDate, setStartDate] = useState<Dayjs | null>(null)
	const [regionId, setRegionId] = useState<number | null>(null)

	const [items, setItems] = useState<FertilizerUsageCrops[]>([])

	useEffect(() => {
		if (props.show) {
			setFieldsFromEntity()

			const initial = props.fertilizerUsage.fertilizerUsageCrops
			const isNew = props.fertilizerUsage.id === 0

			if (initial && initial.length > 0 && !isNew) {
				const withKeys = initial.map(district => {
					const districtCopy = { ...district }

					if (!districtCopy.key) {
						districtCopy.key = crypto.randomUUID()
					}

					if (districtCopy.cropDetails) {
						districtCopy.cropDetails = districtCopy.cropDetails.map(detail => {
							const detailCopy = { ...detail }
							if (!detailCopy.key) {
								detailCopy.key = crypto.randomUUID()
							}
							return detailCopy
						})
					}

					return districtCopy
				})

				setItems(withKeys)
			} else {
				setItems([
					{
						key: crypto.randomUUID(),
						id: null,
						territorialUnitDistrictId: null,
						territorialUnitDistrict: null,
						cropDetails: [
							{
								key: crypto.randomUUID(),
								id: null,
								cropArea: null,
								fertilizerArea: null,
								cropTypeId: null,
								percent: null,
								cropType: null,
							},
						],
					},
				])
			}
		} else {
			form.resetFields()
			setItems([])
			setRegionId(null)
		}
	}, [props.show])

	const setFieldsFromEntity = (): void => {
		const region = props.fertilizerUsage.territorialUnitRegion?.id ?? null
		setRegionId(region)
		form.setFields([
			{
				name: FormField.territorialUnitRegionId,
				value: props.fertilizerUsage.territorialUnitRegion?.id ?? null,
			},
			{
				name: FormField.periodStart,
				value: props.fertilizerUsage.periodStart
					? dayjs(props.fertilizerUsage.periodStart)
					: undefined,
			},
			{
				name: FormField.periodEnd,
				value: props.fertilizerUsage.periodEnd ? dayjs(props.fertilizerUsage.periodEnd) : undefined,
			},
		])
	}

	const setEntityFromFields = (): EntFertilizerUsage => {
		return {
			id: props.fertilizerUsage.id,
			territorialUnitRegionId: form.getFieldValue(FormField.territorialUnitRegionId),
			periodStart: form.getFieldValue(FormField.periodStart)?.format('YYYY-MM-DD'),
			periodEnd: form.getFieldValue(FormField.periodEnd)?.format('YYYY-MM-DD'),
			fertilizerUsageCrops: items.map(crop => {
				// Извлекаем лишние поля: key и territorialUnitDistrict не нужны в payload
				const { key, territorialUnitDistrict, ...cropWithoutExtra } = crop
				return {
					// Чтобы удовлетворить тип, возвращаем поле key с undefined
					key: undefined,
					...cropWithoutExtra,
					cropDetails:
						crop.cropDetails?.map(detail => {
							// Аналогично для деталей – удаляем key и cropType
							const { key, cropType, ...detailWithoutExtra } = detail
							return { key: undefined, ...detailWithoutExtra }
						}) || [],
				}
			}),
		}
	}

	const hideModal = () => {
		props.onHide()
		form.resetFields()
		setItems([])
		setRegionId(null)
	}

	const handleSubmit = async () => {
		setLoading(true)
		try {
			await form.validateFields()
			const data = setEntityFromFields()

			if (!data.fertilizerUsageCrops || data.fertilizerUsageCrops.length === 0) {
				throw new Error('Список районов не должен быть пустым!')
			}

			const invalidRegisters = data.fertilizerUsageCrops.filter(district => {
				if (!district.territorialUnitDistrictId) return true
				if (!district.cropDetails || district.cropDetails.length === 0) return true

				return district.cropDetails.some(detail => {
					return (
						detail.cropTypeId == null || detail.cropArea == null || detail.fertilizerArea == null
					)
				})
			})
			if (invalidRegisters.length > 0) {
				throw new Error('У всех элементов (районов и культур) должны быть заполнены поля!')
			}

			const result = await props.onSubmit(data)
			if (result) {
				hideModal()
			}
		} catch (error: any) {
			message.error(error.message || 'Произошла ошибка при отправке формы.')
		} finally {
			setLoading(false)
		}
	}

	return (
		<DraggableModal
			title={!props.fertilizerUsage.id ? t('general.add') : t('general.edit')}
			visible={props.show}
			onCancel={hideModal}
			onSubmit={handleSubmit}
			submitLoading={loading}
			width={800}
		>
			<Form initialValues={{ remember: true }} autoComplete='off' layout='vertical' form={form}>
				<Row style={{ margin: 0 }} gutter={16}>
					<Col span={12}>
						<Space.Compact>
							<Form.Item
								label={t('general.start_date_period')}
								name={FormField.periodStart}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<DatePicker
									format={dateFormat}
									placeholder={t('general.select_date')}
									onChange={date => setStartDate(date)}
								/>
							</Form.Item>
							<Form.Item
								label={t('general.end_date_period')}
								name={FormField.periodEnd}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<DatePicker
									format={dateFormat}
									placeholder={t('general.select_date')}
									disabledDate={current =>
										!!startDate && current.isBefore(startDate.add(1, 'day'), 'day')
									}
								/>
							</Form.Item>
						</Space.Compact>
					</Col>
					{!props.fertilizerUsage.id && (
						<Col span={12}>
							<Form.Item
								label={t('titles.territorialUnitRegion')}
								name={FormField.territorialUnitRegionId}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<SelectTerritorialUnits
									type={['REGION']}
									onChange={(val: number) => {
										setRegionId(val)
									}}
								/>
							</Form.Item>
						</Col>
					)}

					<Col span={24}>
						<FertilizerUsageItems items={items} setItems={setItems} parentId={regionId} />
					</Col>
				</Row>
			</Form>
		</DraggableModal>
	)
}
